import { createContext, useContext, useEffect, useState } from 'react';
import guestService from '../services/api';

// Create context
const GuestContext = createContext();

// Context provider component
export const GuestProvider = ({ children }) => {
  // State for all guests (loaded on mount)
  const [allGuests, setAllGuests] = useState([]);
  const [isLoadingAllGuests, setIsLoadingAllGuests] = useState(true);
  
  // State for search results
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // State for selected guest group
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [groupGuests, setGroupGuests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

// Load all guests when component mounts
useEffect(() => {
  const loadAllGuests = async () => {
    try {
      setIsLoadingAllGuests(true);
      const guests = await guestService.getAllGuests();
      console.log(`✅ Invitados cargados: ${guests?.length || 0} invitados`);
      console.log('📋 Detalle:', guests);
      setAllGuests(guests || []);
    } catch (err) {
      console.error('❌ Error loading all guests:', err);
      setError('Error al cargar los invitados');
    } finally {
      setIsLoadingAllGuests(false);
    }
  };

  loadAllGuests();
}, []);

  // Normalize strings to make searches accent-insensitive (Jose == José)
  const normalizeString = (value) =>
    (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  // Search for guests locally from allGuests
  const searchGuests = (searchTerm) => {
    setIsSearching(true);
    setError(null);
    
    try {
      if (!searchTerm.trim()) {
        setSearchResults([]);
        setHasSearched(false);
        setIsSearching(false);
        return;
      }

      // Filter guests locally ignoring accents and case
      const normalizedTerm = normalizeString(searchTerm.trim());
      const results = allGuests.filter(guest => {
        const name = normalizeString(guest.name);
        const surname = normalizeString(guest.surname);
        const fullName = `${name} ${surname}`;
        return name.includes(normalizedTerm) || surname.includes(normalizedTerm) || fullName.includes(normalizedTerm);
      });

      setSearchResults(results || []);
      setHasSearched(true);
    } catch (err) {
      console.error('Error searching guests locally:', err);
      setError('Error al buscar invitados.');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Fetch guests from a group
  const fetchGroupGuests = async (guest) => {
    if (!guest || !guest.group || !guest.group.id) {
      setError('No se pudo encontrar el grupo de invitados');
      return;
    }
    
    setSelectedGuest(guest);
    setIsLoading(true);
    setError(null);
    setUpdateSuccess(false); // Reset success state when loading new group
    
    try {
      const groupId = guest.group.id;
      const groupMembers = await guestService.getGuestsByGroup(groupId);
      setGroupGuests(groupMembers || []);
    } catch (err) {
      setError('Error al cargar el grupo de invitados');
    } finally {
      setIsLoading(false);
    }
  };

  // Update guests in a group
  const updateGroupGuests = async (groupId, updatedGuests) => {
    setIsLoading(true);
    setError(null);
    setUpdateSuccess(false);
    
    try {
      const result = await guestService.updateGuestsByGroup(groupId, updatedGuests);
      console.log("API update successful:", result);
      setUpdateSuccess(true);
      return result;
    } catch (err) {
      console.error("API update error:", err);
      setError('Error al actualizar los invitados');
      setUpdateSuccess(false);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form state
  const resetFormState = () => {
    setUpdateSuccess(false);
    setError(null);
  };

  // Clear search
  const clearSearch = () => {
    setSearchResults([]);
    setIsSearching(false);
    setHasSearched(false); // Reset hasSearched state
  };

  // Reset all state
  const resetAll = () => {
    setSearchResults([]);
    setSelectedGuest(null);
    setGroupGuests([]);
    setIsSearching(false);
    setIsLoading(false);
    setError(null);
    setUpdateSuccess(false);
    setHasSearched(false); // Reset hasSearched state
  };

  // Context value
  const value = {
    allGuests,
    isLoadingAllGuests,
    searchResults,
    isSearching,
    hasSearched,
    selectedGuest,
    groupGuests,
    isLoading,
    error,
    updateSuccess,
    searchGuests,
    fetchGroupGuests,
    updateGroupGuests,
    resetFormState,
    clearSearch,
    resetAll
  };

  return <GuestContext.Provider value={value}>{children}</GuestContext.Provider>;
};

// Custom hook for using the context
export const useGuests = () => {
  const context = useContext(GuestContext);
  if (!context) {
    throw new Error('useGuests must be used within a GuestProvider');
  }
  return context;
};

export default GuestContext;